/**
 * Core voxel drawing functions - applies build operations to chunk data.
 *
 * These functions are used by both client (preview + apply) and server (validate + apply).
 */
import { CHUNK_SIZE, VOXEL_SCALE } from './constants.js';
import { BuildMode, BuildShape, clamp, } from './buildTypes.js';
import { applyQuatToVec3Into, invertQuat, rotateXZInto } from '../util/math.js';
import { prepareSdf, sdfPrepared, sdfToWeight } from './shapes.js';
import { coarseWeight, unpackVoxel, setWeight, setMaterial, voxelIndex, chunkKey, getWeight, getMaterial, } from './voxelData.js';
import { isTransparent } from '../materials/Materials.js';
/**
 * How far outside a part's surface `drawToChunk` still evaluates it, world units. A voxel this far
 * out receives a fully-air weight, which the modes mostly ignore — but ADD recolours air that is
 * already air-weighted, so the value is behaviour, not tuning. It was the literal 1.5 in the loop.
 */
const SDF_REACH = 1.5;
// ============== Apply Functions ==============
// These modify a single voxel based on the build mode
/**
 * Rewrite a voxel's weight + material while preserving BOTH light channels (sky AND block). Edits
 * change geometry, not light — the lighting pass owns light. packVoxel() would zero the block-light
 * field, which is harmless for a commit (a relight follows and recomputes it) but wrong for an
 * un-relit preview: in the 'off' preview mode nothing relights until commit, so dropping block light
 * would darken any torch-lit voxel the brush touched. Bit-preserving setWeight/setMaterial keep the
 * existing light untouched.
 */
function repackKeepLight(existingPacked, weight, material) {
    return setMaterial(setWeight(existingPacked, weight), material);
}
/**
 * Apply ADD mode to a voxel.
 * Combines new weight with existing, takes maximum, updates material if weight increases.
 * Solid materials always overwrite transparent materials.
 */
export function applyAdd(existingPacked, newWeight, newMaterial) {
    const existing = unpackVoxel(existingPacked);
    // ADD: take maximum of (existing, existing + new, new)
    const combinedWeight = clamp(Math.max(existing.weight, existing.weight + newWeight, newWeight), -0.5, 0.5);
    // Update material if new weight is >= existing,
    // or if we're overwriting a transparent material with a solid one
    const existingIsTransparent = existing.weight > 0 && isTransparent(existing.material);
    const newIsSolid = !isTransparent(newMaterial);
    const finalMaterial = (newWeight >= existing.weight || (existingIsTransparent && newIsSolid && newWeight > 0))
        ? newMaterial : existing.material;
    const newPacked = repackKeepLight(existingPacked, combinedWeight, finalMaterial);
    return { packed: newPacked, changed: newPacked !== existingPacked };
}
/**
 * Apply SUBTRACT mode to a voxel.
 * Takes minimum weight (carves out material).
 */
export function applySubtract(existingPacked, newWeight, _newMaterial) {
    const existing = unpackVoxel(existingPacked);
    // SUBTRACT: take minimum (newWeight is negative when inside the shape)
    if (newWeight < existing.weight) {
        const newPacked = repackKeepLight(existingPacked, newWeight, existing.material);
        return { packed: newPacked, changed: true };
    }
    return { packed: existingPacked, changed: false };
}
/**
 * Apply PUNCH mode to a voxel: a material-filtered subtract.
 * Carves (takes minimum weight) exactly like SUBTRACT, but only when the existing voxel's material
 * equals `targetMaterial` — voxels of any other material are left untouched. This lets a left-click
 * "punch" dig a blob of only the hit material (e.g. grass) without disturbing neighbouring stone.
 */
/** Weight removed per punch — incremental so digging is gradual (weight is 4-bit quantized). */
const PUNCH_WEIGHT_STEP = 0.25;
export function applyPunch(existingPacked, newWeight, targetMaterial) {
    const existing = unpackVoxel(existingPacked);
    // Only carve voxels of the matched material (material 0 is a real material — moss2 — not air).
    if (existing.material !== targetMaterial) {
        return { packed: existingPacked, changed: false };
    }
    // A voxel the sphere covers has newWeight > 0 (PUNCH keeps the +1 weight multiplier). Carve the
    // whole covered volume uniformly — decrement the stored weight by a fixed step — so the blob is
    // solid (no rim-only shell / centre hole) and incremental.
    if (newWeight <= 0) {
        return { packed: existingPacked, changed: false };
    }
    const carved = Math.max(-0.5, existing.weight - PUNCH_WEIGHT_STEP);
    if (carved >= existing.weight) {
        return { packed: existingPacked, changed: false };
    }
    return { packed: repackKeepLight(existingPacked, carved, existing.material), changed: true };
}
/**
 * Apply PAINT mode to a voxel.
 * Only changes material where weight > 0 (solid areas) and shape overlaps.
 */
export function applyPaint(existingPacked, newWeight, newMaterial) {
    const existing = unpackVoxel(existingPacked);
    // PAINT: only paint where existing is solid AND new weight would make it solid
    if (newWeight > 0 && existing.weight > 0) {
        const newPacked = repackKeepLight(existingPacked, existing.weight, newMaterial);
        return { packed: newPacked, changed: existing.material !== newMaterial };
    }
    return { packed: existingPacked, changed: false };
}
/**
 * Apply FILL mode to a voxel.
 * Only fills where existing weight <= 0 (empty areas) OR
 * where existing material is transparent (allows solid to overwrite transparent).
 */
export function applyFill(existingPacked, newWeight, newMaterial) {
    const existing = unpackVoxel(existingPacked);
    // FILL: fill empty voxels, or overwrite transparent materials with solid ones
    const existingIsTransparent = existing.weight > 0 && isTransparent(existing.material);
    const newIsSolid = !isTransparent(newMaterial);
    if (newWeight > 0 && (existing.weight <= 0 || (existingIsTransparent && newIsSolid))) {
        const finalWeight = Math.max(newWeight, existing.weight);
        const newPacked = repackKeepLight(existingPacked, finalWeight, newMaterial);
        return { packed: newPacked, changed: newPacked !== existingPacked };
    }
    return { packed: existingPacked, changed: false };
}
/**
 * THE PACKED FORM of the apply functions — same rules, no result object, no `unpackVoxel`. Returns
 * the new packed word, which equals `existingPacked` exactly when the object forms would have said
 * `changed: false` (every `changed` above is either `newPacked !== existingPacked` or a condition
 * that implies it). This is the per-voxel path; the object forms stay for readability and tests.
 */
export function applyPacked(mode, existingPacked, newWeight, newMaterial) {
    const ew = getWeight(existingPacked);
    const em = getMaterial(existingPacked);
    switch (mode) {
        case BuildMode.SUBTRACT:
            return newWeight < ew ? repackKeepLight(existingPacked, newWeight, em) : existingPacked;
        case BuildMode.PAINT:
            if (newWeight > 0 && ew > 0 && em !== newMaterial)
                return repackKeepLight(existingPacked, ew, newMaterial);
            return existingPacked;
        case BuildMode.FILL: {
            const existingIsTransparent = ew > 0 && isTransparent(em);
            const newIsSolid = !isTransparent(newMaterial);
            if (newWeight > 0 && (ew <= 0 || (existingIsTransparent && newIsSolid))) {
                return repackKeepLight(existingPacked, Math.max(newWeight, ew), newMaterial);
            }
            return existingPacked;
        }
        case BuildMode.PUNCH: {
            if (em !== newMaterial || newWeight <= 0)
                return existingPacked;
            const carved = Math.max(-0.5, ew - PUNCH_WEIGHT_STEP);
            return carved >= ew ? existingPacked : repackKeepLight(existingPacked, carved, em);
        }
        case BuildMode.ADD:
        default: {
            const combinedWeight = clamp(Math.max(ew, ew + newWeight, newWeight), -0.5, 0.5);
            const existingIsTransparent = ew > 0 && isTransparent(em);
            const newIsSolid = !isTransparent(newMaterial);
            const finalMaterial = (newWeight >= ew || (existingIsTransparent && newIsSolid && newWeight > 0))
                ? newMaterial : em;
            return repackKeepLight(existingPacked, combinedWeight, finalMaterial);
        }
    }
}
/** Registry mapping build modes to their apply functions */
const APPLY_FUNCTIONS = {
    [BuildMode.ADD]: applyAdd,
    [BuildMode.SUBTRACT]: applySubtract,
    [BuildMode.PAINT]: applyPaint,
    [BuildMode.FILL]: applyFill,
    [BuildMode.PUNCH]: applyPunch,
};
/**
 * Get the apply function for a build mode.
 */
export function getApplyFunction(mode) {
    return APPLY_FUNCTIONS[mode] ?? applyAdd;
}
// ============== Bounding Box Calculation ==============
/**
 * Calculate the voxel-space bounding box for a build operation.
 * Returns the range of voxels that could be affected.
 */
export function calculateBuildBBox(operation) {
    const { center, parts } = operation;
    // Convert world center to voxel coordinates
    const centerVoxelX = center.x / VOXEL_SCALE;
    const centerVoxelY = center.y / VOXEL_SCALE;
    const centerVoxelZ = center.z / VOXEL_SCALE;
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    for (const part of parts) {
        const { size } = part.config;
        // Max radius in any direction (conservative estimate for rotated shapes) + margin
        // for rotation, smooth transitions, and 1 extra voxel so surface nets can read the
        // empty neighbor row beyond the SDF boundary.
        const margin = Math.max(size.x, size.y, size.z) + 3;
        // The part sits `offset` (voxel units) from center in the pre-rotation frame; after
        // rotation it can lie |offset| away in any direction, so add its length to the radius.
        const offLen = Math.sqrt(part.offset.x * part.offset.x + part.offset.y * part.offset.y + part.offset.z * part.offset.z);
        const r = margin + offLen;
        minX = Math.min(minX, centerVoxelX - r);
        maxX = Math.max(maxX, centerVoxelX + r);
        minY = Math.min(minY, centerVoxelY - r);
        maxY = Math.max(maxY, centerVoxelY + r);
        minZ = Math.min(minZ, centerVoxelZ - r);
        maxZ = Math.max(maxZ, centerVoxelZ + r);
    }
    return {
        minX: Math.floor(minX),
        minY: Math.floor(minY),
        minZ: Math.floor(minZ),
        maxX: Math.ceil(maxX),
        maxY: Math.ceil(maxY),
        maxZ: Math.ceil(maxZ),
    };
}
/**
 * Get all chunk keys that could be affected by a build operation.
 */
export function getAffectedChunks(operation) {
    const bbox = calculateBuildBBox(operation);
    const chunks = new Set();
    // Convert voxel bounds to chunk bounds
    const minCx = Math.floor(bbox.minX / CHUNK_SIZE);
    const minCy = Math.floor(bbox.minY / CHUNK_SIZE);
    const minCz = Math.floor(bbox.minZ / CHUNK_SIZE);
    const maxCx = Math.floor(bbox.maxX / CHUNK_SIZE);
    const maxCy = Math.floor(bbox.maxY / CHUNK_SIZE);
    const maxCz = Math.floor(bbox.maxZ / CHUNK_SIZE);
    for (let cz = minCz; cz <= maxCz; cz++) {
        for (let cy = minCy; cy <= maxCy; cy++) {
            for (let cx = minCx; cx <= maxCx; cx++) {
                chunks.add(chunkKey(cx, cy, cz));
            }
        }
    }
    return Array.from(chunks);
}
/**
 * Rasterize a `BuildPart[]` (canonical +Y, voxel-unit sizes/offsets) into voxel stamps at identity
 * rotation, reusing the SAME `sdfFromConfig` + `sdfToWeight` stack as `drawToChunk`. This lets
 * terrain generation place any build preset (e.g. the Torch) as a stamp through one shared code
 * path — no duplicated SDF/weight logic. Emits the surface BAND (composite ADD: max weight per voxel,
 * material of the strongest part) — both halves of it, so the crossing lands on the model's real
 * surface. Returns integer voxel offsets from the parts' origin.
 */
export function rasterizePartsToStampVoxels(parts) {
    return rasterizePartsAtScale(parts, 1);
}
/**
 * Half-extents (voxel units) of one part's shape, PER SHAPE — `size` means something different for
 * each (a sphere reads only x, a cylinder reads x as radius and ignores z), so a bbox taken from
 * `size` component-wise both over- and under-covers. Grown by `thickness`, because a hollow shape is
 * inside where |d| < thickness, i.e. up to `thickness` OUTSIDE its own surface.
 */
export function partExtent(config) {
    const s = config.size;
    const t = config.thickness ?? 0;
    switch (config.shape) {
        case BuildShape.SPHERE:
            // size.y is the VERTICAL semi-axis (sdfFromConfig reads it whenever it differs from size.x), so
            // the Y extent is its own number. Every round sphere passes y === x and is unchanged.
            return { x: s.x + t, y: s.y + t, z: s.x + t };
        case BuildShape.CYLINDER:
            return { x: s.x + t, y: s.y + t, z: s.x + t };
        case BuildShape.CONE: {
            const r = Math.max(s.x, s.z);
            return { x: r + t, y: s.y + t, z: r + t };
        }
        default:
            return { x: s.x + t, y: s.y + t, z: s.z + t };
    }
}
/**
 * Rasterize a `BuildPart[]` onto the lattice of a LOD-`log2(scale)` chunk.
 *
 * Sizes and offsets stay in FINE voxels at every level — the same units as every other BuildPart in
 * the codebase — and this function does the level conversion, so ONE part list stays valid for every
 * LOD it serves. The returned offsets are in COARSE voxels, ready for a coarse chunk's grid.
 *
 * WEIGHT IS MEASURED IN COARSE VOXELS, exactly as generateChunkLOD measures terrain — a part one
 * coarse voxel deep reads FULLY SOLID. This matters more than it sounds: `sdfToWeight` ramps over
 * METRES, so `sdfToWeight(d / scale)` needs TWO coarse voxels of depth to saturate, i.e. half the
 * solidity of the terrain a stand-in sits in. Measured on the drawn stand-ins before this: trunks
 * packed at 0.23-0.30 and pine canopies at 0.10-0.43 against a solid 0.50, with an oak's lod-3 crown
 * at 0.03 — one notch above air. Every stand-in surface was sitting in a near-zero-crossing band, so
 * SurfaceNets interpolated its vertices into drifting positions and the shapes read as soft and
 * stepped rather than as geometry. The LOD-0 tree primitives already use this convention
 * (`edgeDist * 0.5`, stamps/primitives.ts); the parts rasterizer was the only thing that did not.
 *
 * scale 1 keeps the historical metre-based ramp: the Torch is rasterized through here at LOD 0 and
 * feeds the stampCache checksum.
 *
 * ADD-only (composite max weight, material of the strongest part), matching the scale-1 behaviour
 * this replaced. The building stand-ins are ADD-only too: a carve's outcome depends on apply order
 * across a whole coarse chunk, and a hollow shell one coarse voxel thick meshes as noise, so a
 * coarse building is its solid massing.
 *
 * `yaw` rotates the WHOLE list about the stamp origin (buildings are scattered at an arbitrary yaw);
 * `BuildPart.yaw` rotates one part about its own offset (the gable's mirrored half). Both are XZ
 * rotations of the query point, and both are skipped when zero, so the LOD-0 Torch path is untouched.
 *
 * `scale === 1` is byte-identical to the original loop — the Torch stamp is rasterized through here
 * and feeds the stampCache checksum.
 */
export function rasterizePartsAtScale(parts, scale, yaw = 0) {
    const out = [];
    if (parts.length === 0)
        return out;
    // Per-part draw state, hoisted: the shape extents and the per-part yaw's cos/sin are the same for
    // every lattice point. `anyYaw` decides whether the walk needs a rotation-proof bbox at all.
    const yawCos = Math.cos(yaw), yawSin = Math.sin(yaw);
    const prepared = parts.map((part) => {
        const e = partExtent(part.config);
        // A part rotated about its own offset sweeps a disc there, so its X and Z extents merge — the
        // gable's mirrored half is a yaw of π, where the box happens to be symmetric anyway, but any other
        // angle would be clipped along the shape's short axis.
        const extent = part.yaw ? { x: Math.max(e.x, e.z), y: e.y, z: Math.max(e.x, e.z) } : e;
        return {
            config: part.config,
            sdf: prepareSdf(part.config),
            material: part.config.material,
            offset: part.offset,
            extent,
            cos: part.yaw ? Math.cos(part.yaw) : 1,
            sin: part.yaw ? Math.sin(part.yaw) : 0,
            rotated: !!part.yaw,
        };
    });
    const anyYaw = yaw !== 0;
    // Integer-voxel bbox over all parts, in FINE voxels, from the real per-shape extents. It only has
    // to cover where the SDF is INSIDE — `sdfToWeight` is positive iff d < 0, so a lattice point
    // outside every shape emits nothing and walking it is pure cost. The old bbox padded each part by
    // `max(size) + 2`, which is a rounding margin for a tree and a doubling for a building: a lod-1
    // house walked a 100-fine-voxel square for a 48-voxel body, i.e. 4x the lattice points for
    // byte-identical output.
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    for (const p of prepared) {
        const e = p.extent, o = p.offset;
        minX = Math.min(minX, o.x - e.x);
        maxX = Math.max(maxX, o.x + e.x);
        minY = Math.min(minY, o.y - e.y);
        maxY = Math.max(maxY, o.y + e.y);
        minZ = Math.min(minZ, o.z - e.z);
        maxZ = Math.max(maxZ, o.z + e.z);
    }
    if (anyYaw) {
        // A yawed shape sweeps a disc about the yaw axis (the stamp origin), so X and Z share ONE
        // half-extent — and it is the DIAGONAL, not the larger side. A 6 m square plinth turned 45° puts
        // its corner 8.5 m out; taking the side clipped the corners off every rotated building (visible as
        // a bitten-off foundation, and it is the same subtlety that makes the cull radius a hypotenuse).
        let hr = 0;
        for (const p of prepared) {
            hr = Math.max(hr, Math.hypot(Math.abs(p.offset.x) + p.extent.x, Math.abs(p.offset.z) + p.extent.z));
        }
        minX = minZ = -hr;
        maxX = maxZ = hr;
    }
    // The bbox is computed in fine voxels (above) then walked on the coarse lattice; the margin absorbs
    // the fine→coarse rounding AND the band outside the model. The band is one coarse voxel wide where
    // `coarseWeight` measures it and TWO where `sdfToWeight` does (it ramps over half a metre, which is
    // two fine voxels — the metre-vs-lattice mismatch foldDense's header already complains about).
    const margin = scale === 1 ? 3 : 2;
    const x0 = Math.floor(minX / scale) - margin, x1 = Math.ceil(maxX / scale) + margin;
    const y0 = Math.floor(minY / scale) - margin, y1 = Math.ceil(maxY / scale) + margin;
    const z0 = Math.floor(minZ / scale) - margin, z1 = Math.ceil(maxZ / scale) + margin;
    const cull = 1.5 * scale;
    const pp = { x: 0, z: 0 };
    const yawScratch = { x: 0, z: 0 };
    for (let y = y0; y <= y1; y++) {
        for (let x = x0; x <= x1; x++) {
            for (let z = z0; z <= z1; z++) {
                // Inverse-rotate the query point into the stamp's own (unyawed) space, once per lattice
                // point rather than once per part.
                //
                // THE SIGN IS LOD 0'S, NOT A CHOICE. The LOD-0 building generators sample their SDF through
                // `rotateXZ(x, z, cos, -sin)`, i.e. the query goes through +yaw. This ran the other way for
                // its whole life, so every coarse building was the MIRROR of its LOD-0 self — wrong by 2*yaw,
                // right only near 0 and +-90 degrees, which is exactly "the rotation is sometimes wrong".
                // Measured on the rectangular house: LOD 0's footprint axis at yaw 0.4 is +67.1 deg and the
                // lod-1 stand-in's was -67.9. The guard is now a principal-axis comparison at yaws where the
                // sign shows (lodStandIn.test.ts); the old case used 90 degrees, where it cannot.
                const fx = x * scale, fz = z * scale;
                if (anyYaw)
                    rotateXZInto(yawScratch, fx, fz, yawCos, yawSin);
                const sx = anyYaw ? yawScratch.x : fx;
                const sz = anyYaw ? yawScratch.z : fz;
                // STARTS AT AIR, NOT AT SOLID. Keeping only `bestWeight > 0` emitted the inside half of the
                // band and left the outside saturated at -0.5, so every crossing was pulled toward the solid
                // and the model came out SHRUNKEN — measured on a radius-8 sphere: drawn at 7.59 fine voxels
                // at lod 0, 6.40 at lod 2 and 3.25 at lod 3, i.e. 41% of its own radius at range. That is why
                // stamps read blocky in the distance. See voxel/surfaceBand.ts.
                let bestWeight = -0.5;
                let bestMat = 0;
                for (const part of prepared) {
                    // Part-local position in world metres (the prepared SDF holds sizes in world units).
                    const lx = sx - part.offset.x;
                    const lz = sz - part.offset.z;
                    // Same convention as the whole-list yaw above (immaterial for the gable's pi, but the two
                    // must mean one thing or a future part yaw would turn the wrong way).
                    if (part.rotated)
                        rotateXZInto(pp, lx, lz, part.cos, part.sin);
                    const px = (part.rotated ? pp.x : lx) * VOXEL_SCALE;
                    const py = (y * scale - part.offset.y) * VOXEL_SCALE;
                    const pz = (part.rotated ? pp.z : lz) * VOXEL_SCALE;
                    // A point further than `cull` outside the part's box is further than `cull` from the part
                    // (every SDF here is at least the distance to its bounding box), so it would fail the cull
                    // below anyway — skip the evaluation. Output-identical; most points of a composite are
                    // outside most of its parts.
                    const sd = part.sdf;
                    if (Math.abs(px) > sd.ex + cull || Math.abs(py) > sd.ey + cull || Math.abs(pz) > sd.ez + cull)
                        continue;
                    const d = sdfPrepared(px, py, pz, sd);
                    if (d > cull)
                        continue;
                    // d is metres; /VOXEL_SCALE → fine voxels, /scale → coarse voxels, *0.5 → the terrain ramp.
                    // scale 1 keeps the historical metre ramp (the Torch's LOD-0 identity); above it the ramp is
                    // the lattice's own, which is what `coarseWeight` names — expressed here as a distance in
                    // fine voxels so the two paths read as the one convention they are.
                    // `d` is a signed SDF (negative inside) and coarseWeight takes a DEPTH, hence the negation.
                    const w = scale === 1 ? sdfToWeight(d) : coarseWeight(-d / VOXEL_SCALE, scale);
                    if (w > bestWeight) {
                        bestWeight = w;
                        bestMat = part.material;
                    }
                }
                // The band carries the nearest part's MATERIAL even where it is air, and must: a negative
                // sample with material 0 is the explicit interior carve StampPlacer keys on, and emitting the
                // band as one would eat the terrain around every tree.
                if (bestWeight > -0.5)
                    out.push({ x, y, z, material: bestMat, weight: bestWeight });
            }
        }
    }
    return out;
}
// ============== Core Drawing Function ==============
/**
 * Draw a build operation to a single chunk's data array.
 *
 * @param chunk The chunk to modify
 * @param operation The build operation
 * @param targetData Optional data array to write to (defaults to chunk.data)
 * @param changedBounds Optional out-parameter: the local voxel box this call actually WROTE,
 *   left empty (x1 < x0) when nothing changed. The operation's own bbox is a conservative
 *   `max(size) + 3 + |offset|` sphere around the centre — fine for deciding which chunks to load,
 *   ~10x too big for anything that has to STORE the touched region (the LOD edit cascade folds it
 *   into a layer, and paid a 10x storage and dilation penalty for the margin).
 * @returns True if any voxels were changed
 */
export function drawToChunk(chunk, operation, targetData = chunk.data, changedBounds) {
    if (changedBounds) {
        changedBounds.x0 = changedBounds.y0 = changedBounds.z0 = CHUNK_SIZE;
        changedBounds.x1 = changedBounds.y1 = changedBounds.z1 = -1;
    }
    const { center, rotation } = operation;
    // Get the inverse rotation to transform voxel positions into shape space
    const invRotation = invertQuat(rotation);
    // Get chunk world position
    const chunkWorldPos = chunk.getWorldPosition();
    // Precompute per-part draw state. All parts share `rotation`, so in shape-local space
    // a part is just a translation by its offset (voxel units → world units). One
    // inverse-rotate per voxel is reused across every part. The SDF is prepared once per part
    // (world-unit sizes, extents) so the per-voxel work is arithmetic only.
    const prepared = operation.parts.map((part) => ({
        mode: part.config.mode,
        weightMult: part.config.mode === BuildMode.SUBTRACT ? -1 : 1,
        material: part.config.material,
        sdf: prepareSdf(part.config),
        ox: part.offset.x * VOXEL_SCALE,
        oy: part.offset.y * VOXEL_SCALE,
        oz: part.offset.z * VOXEL_SCALE,
        // Per-part yaw about its own offset — the mirror the shared operation rotation cannot express.
        cos: part.yaw ? Math.cos(part.yaw) : 1,
        sin: part.yaw ? Math.sin(part.yaw) : 0,
        rotated: !!part.yaw,
    }));
    // THE VOXELS THIS OPERATION CAN TOUCH, and only those. A part is processed where its SDF is within
    // SDF_REACH of its surface, so every voxel it can change lies within (part extent + |offset| +
    // SDF_REACH) of the operation centre in any orientation. Walking the whole chunk for a 2 m brush
    // visited 32k voxels to change 4k of them — measured 63 ms per chunk per stroke, 8x this.
    let reach = 0;
    for (const part of prepared) {
        // The prism's cull extents are unbounded (see prepareSdf); its real half-extents are its sizes.
        const sd = part.sdf;
        const e = Math.max(sd.sx, sd.sy, sd.sz) + sd.thickness;
        const off = Math.sqrt(part.ox * part.ox + part.oy * part.oy + part.oz * part.oz);
        reach = Math.max(reach, e + off + SDF_REACH);
    }
    const reachVox = reach / VOXEL_SCALE;
    const cxv = (center.x - chunkWorldPos.x) / VOXEL_SCALE;
    const cyv = (center.y - chunkWorldPos.y) / VOXEL_SCALE;
    const czv = (center.z - chunkWorldPos.z) / VOXEL_SCALE;
    const lx0 = Math.max(0, Math.floor(cxv - reachVox)), lx1 = Math.min(CHUNK_SIZE - 1, Math.ceil(cxv + reachVox));
    const ly0 = Math.max(0, Math.floor(cyv - reachVox)), ly1 = Math.min(CHUNK_SIZE - 1, Math.ceil(cyv + reachVox));
    const lz0 = Math.max(0, Math.floor(czv - reachVox)), lz1 = Math.min(CHUNK_SIZE - 1, Math.ceil(czv + reachVox));
    if (lx0 > lx1 || ly0 > ly1 || lz0 > lz1)
        return false;
    let anyChanged = false;
    // Scratch reused per voxel: the shape-local position, and a part's yawed XZ.
    const localPos = { x: 0, y: 0, z: 0 };
    const partXZ = { x: 0, z: 0 };
    for (let lz = lz0; lz <= lz1; lz++) {
        for (let ly = ly0; ly <= ly1; ly++) {
            for (let lx = lx0; lx <= lx1; lx++) {
                // Position relative to build center (voxel centre in world units), inverse-rotated into the
                // shape's local space — shared across all parts.
                applyQuatToVec3Into(localPos, chunkWorldPos.x + lx * VOXEL_SCALE - center.x, chunkWorldPos.y + ly * VOXEL_SCALE - center.y, chunkWorldPos.z + lz * VOXEL_SCALE - center.z, invRotation);
                // Fold every part into this voxel in order (composite atomically).
                const idx = voxelIndex(lx, ly, lz);
                const original = targetData[idx];
                let current = original;
                for (const part of prepared) {
                    // Part-local position = shape-local position minus the part's offset, then inverse-rotated
                    // by the part's own yaw (identity for every part that does not set one).
                    let px = localPos.x - part.ox;
                    let pz = localPos.z - part.oz;
                    if (part.rotated) {
                        rotateXZInto(partXZ, px, pz, part.cos, part.sin);
                        px = partXZ.x;
                        pz = partXZ.z;
                    }
                    const py = localPos.y - part.oy;
                    // Outside the part's box by more than the reach → outside the reach of its SDF too.
                    const sd = part.sdf;
                    if (Math.abs(px) > sd.ex + SDF_REACH || Math.abs(py) > sd.ey + SDF_REACH || Math.abs(pz) > sd.ez + SDF_REACH)
                        continue;
                    const sdfDist = sdfPrepared(px, py, pz, sd);
                    // Only process voxels near this part's surface.
                    if (sdfDist > SDF_REACH)
                        continue;
                    current = applyPacked(part.mode, current, sdfToWeight(sdfDist) * part.weightMult, part.material);
                }
                if (current !== original) {
                    targetData[idx] = current;
                    anyChanged = true;
                    if (changedBounds) {
                        if (lx < changedBounds.x0)
                            changedBounds.x0 = lx;
                        if (lx > changedBounds.x1)
                            changedBounds.x1 = lx;
                        if (ly < changedBounds.y0)
                            changedBounds.y0 = ly;
                        if (ly > changedBounds.y1)
                            changedBounds.y1 = ly;
                        if (lz < changedBounds.z0)
                            changedBounds.z0 = lz;
                        if (lz > changedBounds.z1)
                            changedBounds.z1 = lz;
                    }
                }
            }
        }
    }
    return anyChanged;
}
/**
 * Draw a build operation to multiple chunks.
 *
 * @param chunks Map of chunk key to ChunkData
 * @param operation The build operation
 * @returns Array of chunk keys that were modified
 */
export function drawToChunks(chunks, operation) {
    const affectedKeys = getAffectedChunks(operation);
    const modifiedKeys = [];
    for (const key of affectedKeys) {
        const chunk = chunks.get(key);
        if (chunk) {
            const changed = drawToChunk(chunk, operation);
            if (changed) {
                modifiedKeys.push(key);
            }
        }
    }
    return modifiedKeys;
}
/**
 * Create a simple build operation from position and config.
 * Uses identity rotation (no rotation).
 */
export function createBuildOperation(centerX, centerY, centerZ, config) {
    return {
        center: { x: centerX, y: centerY, z: centerZ },
        rotation: { x: 0, y: 0, z: 0, w: 1 }, // Identity quaternion
        parts: [{ config, offset: { x: 0, y: 0, z: 0 } }],
    };
}
//# sourceMappingURL=drawing.js.map